import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { BinIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useRemoveExpensePlanMutation } from "@services/budget/mutations";

interface IRemoveExpensePlanModalProps extends IUseModalProps {
  expensePlanId: number;
  planName: string;
}

export const RemoveExpensePlanModal: React.FC<IRemoveExpensePlanModalProps> = (
  props,
) => {
  const { expensePlanId, planName, ...rest } = props;
  const { t } = useTranslation();
  const modal = useModal(rest);

  const { mutateAsync: removeExpense, isPending: isRemovePending } =
    useRemoveExpensePlanMutation();

  const { toastApiError } = useApiErrorUtils();

  const handleSubmit = async () => {
    const response = await removeExpense({
      expensePlanId: expensePlanId,
    }).catch(toastApiError);

    if (response) {
      toast.success(t("budget.expenses.editExpensePlanModal.remove.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      className="md:max-w-xl"
      badgeProps={{ icon: BinIcon, color: "red" }}
      title={t("settings.budget.removeModal.title")}
      subtitle={planName}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("settings.budget.removeModal.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit}
            isLoading={isRemovePending}
          >
            {t("settings.budget.removeModal.confirm")}
          </Button>
        </React.Fragment>
      }
    ></Modal>
  );
};
